import {
  makeApiProvider,
  useApiQuery,
} from '@pflegenavi/shared-frontend/platform';
import { createContext } from 'react';
import type { IStripePhoenixApi, CreateStripeAccountResponse } from './api';
import { StripePhoenixApi } from './api';
import type {
  UseQueryOptions,
  UseQueryResult,
  UseMutationOptions,
  UseMutationResult,
} from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { ServiceProviderStripeState } from '@pflegenavi/service-provider-shared/api-endpoints';

const ApiContext = createContext<IStripePhoenixApi | undefined>(undefined);
const { useApi: useStripeApi, ApiProvider: StripePhoenixApiProvider } =
  makeApiProvider({
    name: 'Stripe',
    ApiContext,
    newApi: (tenantId, auth, apiUrl) =>
      new StripePhoenixApi(tenantId, auth, apiUrl),
  });

export { StripePhoenixApiProvider };

const GET_SERVICE_PROVIDER_STRIPE_INFO_KEY = (
  globalServiceProviderId: string | undefined
) => [`stripe-info`, globalServiceProviderId];

export const useGetServiceProviderStripeStatePhoenix = (
  params: {
    globalServiceProviderId: string | undefined;
  },
  options?: Omit<
    UseQueryOptions<ServiceProviderStripeState, unknown>,
    'queryFn' | 'queryKey'
  >
): UseQueryResult<ServiceProviderStripeState, unknown> => {
  const api = useStripeApi();
  return useApiQuery(
    () => api,
    GET_SERVICE_PROVIDER_STRIPE_INFO_KEY(params.globalServiceProviderId),
    (api) => api.getServiceProviderStripeState(),
    options
  );
};

export const useCreateStripeAccountPhoenix = (
  options?: Omit<
    UseMutationOptions<CreateStripeAccountResponse, unknown, void>,
    'mutationFn'
  >
): UseMutationResult<CreateStripeAccountResponse, unknown, void> => {
  const api = useStripeApi();
  return useMutation({
    mutationFn: () => api.createStripeAccount(),
    ...options,
  });
};

export const useRefreshStripeAccountPhoenix = (
  options?: Omit<
    UseMutationOptions<CreateStripeAccountResponse, unknown, void>,
    'mutationFn'
  >
): UseMutationResult<CreateStripeAccountResponse, unknown, void> => {
  const api = useStripeApi();
  return useMutation({
    mutationFn: () => api.refreshStripeAccount(),
    ...options,
  });
};
